import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close as CloseIcon, EditOutlined as EditIcon, RocketLaunchOutlined as LaunchIcon, Undo as UndoIcon } from '@mui/icons-material';
import { comptoirServicesAPI } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { ComptoirServiceDetail } from '../../../types/comptoir';
import { COLOR, dh, fDate, num, primaryBtnSx } from '../ui';

const Head: React.FC<{ icon: React.ReactNode; title: string; sub: string; onClose: () => void; color?: string; id: string }> = ({ icon, title, sub, onClose, color = COLOR, id }) => (
  <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
    <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha(color, 0.12), color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography id={id} variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{sub}</Typography>
    </Box>
    <IconButton onClick={onClose} aria-label="Fermer"><CloseIcon /></IconButton>
  </Box>
);

/* ---------------- Modifier (nature, priorité, deadline, note) ---------------- */
export const ServiceEditDialog: React.FC<{
  open: boolean; service: ComptoirServiceDetail; onClose: () => void; onSaved: (s: ComptoirServiceDetail) => void;
}> = ({ open, service, onClose, onSaved }) => {
  const [f, setF] = useState({ nature: service.nature, priorite: service.priorite, deadline: service.deadline || '', description: service.description || '' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setF({ nature: service.nature, priorite: service.priorite, deadline: service.deadline || '', description: service.description || '' });
      setErr('');
    }
  }, [open, service]);
  const save = async () => {
    setSaving(true); setErr('');
    try {
      onSaved(await comptoirServicesAPI.update(service.id, { ...f, deadline: f.deadline || null, description: f.description.trim() || null }));
    } catch (e: any) { setErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  const late = !!f.deadline && f.deadline < new Date().toISOString().split('T')[0];
  // A Service is in the SRV counter, a Projet in the PRJ one (Gestion Projets / Services): new number
  const newPrefix = f.nature !== service.nature ? (f.nature === 'Projet' ? 'PRJ' : 'SRV') : '';
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth aria-labelledby="comptoir-sedit-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="comptoir-sedit-title" icon={<EditIcon />} title={`Modifier ${service.reference}`} sub="Pour changer les travaux, la matière ou les prix : « Modifier les travaux »" onClose={onClose} />
      <DialogContent sx={{ pt: '20px !important' }}>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Nature</Typography>
            <ToggleButtonGroup exclusive size="small" value={f.nature} onChange={(_, v) => v && setF({ ...f, nature: v })}
              sx={{ '& .Mui-selected': { bgcolor: `${alpha(COLOR, 0.12)} !important`, color: `${COLOR} !important` } }}>
              <ToggleButton value="Service">Service</ToggleButton>
              <ToggleButton value="Projet">Projet</ToggleButton>
            </ToggleButtonGroup>
            {newPrefix && (
              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.75 }}>
                Nouveau numéro <b>{newPrefix}…</b> dans Gestion Projets / Services. {service.reference} reste dans l'historique.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Priorité" fullWidth size="small" value={f.priorite} onChange={(e) => setF({ ...f, priorite: e.target.value as any })}>
              <MenuItem value="normale">Normale</MenuItem>
              <MenuItem value="urgente">Urgente</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Livraison promise (deadline)" type="date" fullWidth InputLabelProps={{ shrink: true }} value={f.deadline}
              onChange={(e) => setF({ ...f, deadline: e.target.value })}
              helperText={late ? 'Date déjà passée' : service.date_souhaitee ? `Souhaitée par le client : ${fDate(service.date_souhaitee)}` : ' '}
              FormHelperTextProps={{ sx: late ? { color: 'warning.main' } : undefined }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Note interne" fullWidth multiline minRows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving} sx={{ ...primaryBtnSx, minWidth: 140 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- Étape précédente ---------------- */
type RetourOpt = { k: 'rouvrir' | 'annulBL' | 'beRenvoyer' | 'beAttente' | 'annulLancement'; label: string; effects: string[]; livraisonId?: number };

export function retourOptions(s: ComptoirServiceDetail): RetourOpt[] {
  const out: RetourOpt[] = [];
  if (s.etat === 'cloture') {
    const what = s.nature === 'Projet' ? 'le projet' : 'le service';
    out.push({ k: 'rouvrir', label: `Rouvrir ${what} (revenir à « Livré »)`, effects: [`${s.nature === 'Projet' ? 'Le projet' : 'Le service'} redevient « Livré ».`, 'Aucun paiement ni livraison n\'est modifié.'] });
    return out;
  }
  const active = s.livraisons.filter((l) => !l.annule);
  const last = active[active.length - 1];
  if (last) {
    out.push({
      k: 'annulBL', livraisonId: last.id, label: `Annuler la dernière livraison (${last.reference} du ${fDate(last.date)})`,
      effects: [`Le bon ${last.reference} reste dans l'historique, marqué « annulé ».`, 'Les quantités livrées sont recalculées.', 'Les paiements ne changent pas.'],
    });
  }
  // Bureau d'études (prototype « Étape précédente »)
  if (s.be && ['valide', 'sans_plan'].includes(s.be.statut)) {
    out.push({
      k: 'beRenvoyer', label: 'Renvoyer le plan au bureau d\'études',
      effects: ['Le plan repasse « En cours de conception ».', 'Les étapes de fabrication datées et pas commencées perdent leur créneau.', 'La planification est de nouveau avertie.'],
    });
  }
  if (s.be?.statut === 'conception') {
    out.push({
      k: 'beAttente', label: 'Remettre en attente du bureau d\'études',
      effects: ['La conception repasse « En attente du BE ».'],
    });
  }
  if (s.lance_le && !['livre', 'livre_partiel'].includes(s.etat)) {
    out.push({
      k: 'annulLancement', label: `Annuler le lancement en production (du ${fDate(s.lance_le)})`,
      effects: [`${s.nature === 'Projet' ? 'Le projet' : 'Le service'} redevient « À lancer » et sort du bureau d'études.`,
        'Refusé s\'il est déjà au planning, au suivi ou a des étapes de fabrication.', 'Les paiements ne changent pas.'],
    });
  }
  return out;
}

export const RetourDialog: React.FC<{
  open: boolean; service: ComptoirServiceDetail; onClose: () => void; onSaved: (s: ComptoirServiceDetail) => void;
}> = ({ open, service, onClose, onSaved }) => {
  const opts = retourOptions(service);
  const [choice, setChoice] = useState<string>('');
  const [motif, setMotif] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setChoice(opts[0]?.k || ''); setMotif(''); setSubmitted(false); setErr(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const opt = opts.find((o) => o.k === choice);
  const motifErr = submitted && !motif.trim() ? 'Indiquez le motif : il est gardé dans l\'historique.' : '';
  const save = async () => {
    setSubmitted(true); setErr('');
    if (!opt || !motif.trim()) return;
    setSaving(true);
    try {
      const m = motif.trim();
      const s = opt.k === 'rouvrir' ? await comptoirServicesAPI.rouvrir(service.id, m)
        : opt.k === 'beRenvoyer' ? await comptoirServicesAPI.beRenvoyer(service.id, m)
          : opt.k === 'beAttente' ? await comptoirServicesAPI.beAttente(service.id, m)
            : opt.k === 'annulLancement' ? await comptoirServicesAPI.annulerLancement(service.id, m)
              : await comptoirServicesAPI.annulerLivraison(opt.livraisonId!, m);
      onSaved(s);
    } catch (e: any) { setErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth aria-labelledby="comptoir-retour-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="comptoir-retour-title" icon={<UndoIcon />} title="Revenir à l'étape précédente" sub={`${service.reference} · rien n'est effacé de l'historique`} onClose={onClose} color="#ED6C02" />
      <DialogContent sx={{ pt: '20px !important' }}>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {!opts.length ? (
          <Alert severity="info">
            Aucun retour possible à cette étape. Pour corriger les travaux ou les prix, utilisez « Modifier les travaux » (une nouvelle version est créée).
          </Alert>
        ) : (
          <>
            <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)}>
              {opts.map((o) => (
                <Paper key={o.k} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2, borderColor: choice === o.k ? '#ED6C02' : 'divider' }}>
                  <FormControlLabel value={o.k} control={<Radio sx={{ '&.Mui-checked': { color: '#ED6C02' } }} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{o.label}</Typography>} />
                  <Box component="ul" sx={{ m: 0, pl: 6, color: 'text.secondary', fontSize: 13 }}>{o.effects.map((t) => <li key={t}>{t}</li>)}</Box>
                </Paper>
              ))}
            </RadioGroup>
            <TextField label="Motif" required fullWidth sx={{ mt: 1.5 }} autoFocus value={motif} onChange={(e) => setMotif(e.target.value)}
              placeholder="ex : pièces refusées par le client, erreur de clôture…" error={!!motifErr} helperText={motifErr || ' '} />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>{opts.length ? 'Annuler' : 'Fermer'}</Button>
        {opts.length > 0 && (
          <Button variant="contained" color="warning" onClick={save} disabled={saving} sx={{ minWidth: 180 }}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <UndoIcon />}>Confirmer le retour</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- Lancer en production (prototype) ---------------- */
export const LancerDialog: React.FC<{
  open: boolean; service: ComptoirServiceDetail; peutForcer: boolean; onClose: () => void; onSaved: (s: ComptoirServiceDetail) => void;
}> = ({ open, service: s, peutForcer, onClose, onSaved }) => {
  const [instructions, setInstructions] = useState('');
  const [forcer, setForcer] = useState(false);
  const [motif, setMotif] = useState('');
  const [dessinateur, setDessinateur] = useState<number | ''>('');
  const [dessinateurs, setDessinateurs] = useState<{ id: number; full_name: string }[]>([]);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setInstructions(s.description || ''); setForcer(false); setMotif(''); setErr(''); setDessinateur('');
      comptoirServicesAPI.meta().then((m) => setDessinateurs(m.dessinateurs || [])).catch(() => setDessinateurs([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const paid = num(s.paye) > 0;
  const mats = s.offre.matieres;
  const save = async () => {
    setErr('');
    if (!paid && !forcer) { setErr(peutForcer ? "L'avance n'est pas reçue : encaissez-la, ou cochez « Lancer quand même »." : "L'avance n'est pas encore reçue : le lancement attend son encaissement."); return; }
    if (!paid && !motif.trim()) { setErr('Indiquez le motif du lancement sans avance.'); return; }
    setSaving(true);
    try { onSaved(await comptoirServicesAPI.lancer(s.id, { instructions: instructions.trim() || null, forcer: !paid && forcer, motif: motif.trim() || null,
                                                       dessinateur_id: dessinateur || null })); }
    catch (e: any) { setErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth aria-labelledby="comptoir-lancer-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="comptoir-lancer-title" icon={<LaunchIcon />} title={`Lancer ${s.reference} en production`}
        sub={`${s.titre} · ${s.client_nom || ''} · ${s.nature}`} onClose={onClose} />
      <DialogContent sx={{ pt: '20px !important' }}>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {paid
          ? <Alert severity="success" sx={{ mb: 2 }}>Avance reçue : {dh(s.paye)}.</Alert>
          : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              L'avance n'est pas encore encaissée. Normalement, le projet part en production après l'avance.
              {peutForcer && (
                <FormControlLabel sx={{ display: 'flex', mt: 0.5 }} control={<Checkbox checked={forcer} onChange={(e) => setForcer(e.target.checked)} />}
                  label="Lancer quand même" />
              )}
            </Alert>
          )}
        {!paid && forcer && (
          <TextField label="Motif du lancement sans avance" required fullWidth sx={{ mb: 2 }} value={motif} onChange={(e) => setMotif(e.target.value)}
            placeholder="ex : client habituel, avance promise demain" />
        )}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Transmis à la production</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2, fontSize: 14 }}>
          <li>Référence <b>{s.reference}</b> (inchangée)</li>
          <li>Travaux : {s.travaux.map((t) => `${num(t.quantite)} ${t.unite || 'pièce'} · ${t.libelle}`).join(' ; ')}</li>
          <li>Matière du client : {mats.length ? mats.map((m) => `${m.designation} (${m.date_reception ? 'reçue' : 'à recevoir'})`).join(', ') : 'aucune'}</li>
          <li>Livraison promise : {fDate(s.deadline)}{s.priorite === 'urgente' ? ' · urgent' : ''}</li>
        </Box>
        <TextField select label="Dessinateur" fullWidth sx={{ mb: 2 }} value={dessinateur}
          onChange={(e) => setDessinateur(e.target.value === '' ? '' : Number(e.target.value))}
          helperText={dessinateur ? 'Il reçoit une notification et retrouve la fiche dans son bureau d\'études.'
            : 'Facultatif : sinon le premier dessinateur qui commence la conception la prend.'}>
          <MenuItem value=""><em>Non assigné</em></MenuItem>
          {dessinateurs.map((u) => <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>)}
        </TextField>
        <TextField label="Instructions pour la production et le bureau d'études" fullWidth multiline minRows={3}
          value={instructions} onChange={(e) => setInstructions(e.target.value)}
          placeholder="Ce que le client a demandé, contraintes, pièces de référence…" helperText="Imprimées sur la fiche A3 du bureau d'études" />
        <Alert severity="info" sx={{ mt: 1 }}>
          Le projet part au bureau d'études (« En attente du BE »). Un projet ne se lance qu'une fois ; « Étape précédente » permet d'annuler le lancement.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving || (!paid && !peutForcer)} sx={{ ...primaryBtnSx, minWidth: 200 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <LaunchIcon />}>Lancer en production</Button>
      </DialogActions>
    </Dialog>
  );
};
